import {
    Component,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewChildren,
    QueryList,
    forwardRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    HostBinding
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Client } from '@intranet/modules/clients';

interface FlatOption {
    type: 'all' | 'groupLabel' | 'client' | 'child' | 'divider';
    label?: string;
    clientId?: number;
    groupId?: number;
    /** groupLabel only — member count, rendered separately on the far right. */
    count?: number;
    /** child only — last item of an expanded group (draws the group-end line). */
    groupEnd?: boolean;
}

@Component({
    selector: 'client-search',
    templateUrl: './client-search.component.html',
    styleUrls: ['./client-search.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => ClientSearchComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientSearchComponent implements OnInit, OnChanges, ControlValueAccessor {
    @Input() clients: [Client[], Client[]] = [[], []];
    @Input() allowAll = true;
    @Input() allLabel = 'All';
    @Input() disabled = false;
    @Input() multi = false;
    @Input() minDropdownWidth: number = 0;
    @Output() changed = new EventEmitter<number | null>();
    @Input() showApply = false;
    @Output() applySelection = new EventEmitter<number[]>(); // for multi-select mode

    /** Multi-select only. When true, clicking "All" checks every individual
     *  client (instead of clearing the selection), so the user can then
     *  uncheck the few they want to exclude. Off by default to keep existing
     *  usages of this shared component unchanged. */
    @Input() selectAllChecksEveryClient = false;
    /** Fires alongside applySelection (before it) whenever selectAllChecksEveryClient
     *  is on: true when the "All" checkbox has been explicitly unchecked (no
     *  clients selected), false otherwise. An empty applySelection array is
     *  ambiguous between "no filter" and "filtered to nothing" — consumers that
     *  care about that distinction (e.g. to show zero results) should listen here. */
    @Output() allClientsDeselected = new EventEmitter<boolean>();
    /** The consumer's own durable copy of the last allClientsDeselected value
     *  (e.g. persisted in a URL param), fed back in as the single source of
     *  truth for the resting (dropdown-closed) state. Needed because an empty
     *  ngModel value round-tripping back through the consumer (e.g. via a
     *  router navigation) re-invokes writeValue() with a value that's
     *  indistinguishable from the default empty selection — this input is
     *  what keeps "explicitly deselected" from being silently lost then. */
    @Input() explicitlyDeselected = false;

    @ViewChild('trigger') trigger!: CdkOverlayOrigin;
    @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
    @ViewChildren('option') optionRefs!: QueryList<ElementRef<HTMLElement>>;

    @HostBinding('class.disabled') get isDisabled() {
        return this.disabled;
    }

    dropdownOpen = false;
    dropUp = false;
    overlayWidth = 0;

    searchTerm = '';
    searchInput$ = new Subject<string>();
    filtered: [Client[], Client[]] = [[], []];
    activeGroups = new Set<number>();
    highlightedIndex = 0;
    optionsList: FlatOption[] = [];

        private _singleValue: number | '' = '';
    private _multiValue: number[] = [];
    private _pendingMultiValue: number[] | null = null;

    /** selectAllChecksEveryClient only. Distinguishes an empty selection that
     *  means "explicitly deselected everything" (All unchecked, nothing
     *  checked) from the default empty selection, which implicitly means
     *  "All" (everything shown checked). */
    private allDeselected = false;
    private _pendingAllDeselected: boolean | null = null;

    get value(): number | number[] | '' {
        return this.multi ? this._multiValue : this._singleValue;
    }

    set value(v: number | number[] | '') {
        if (this.multi) {
            const arr = Array.isArray(v)
                ? v
                : (v === '' || v == null ? [] : [v as number]);
            this._multiValue = arr;
            this.onChange(arr);
        } else {
            const val = (typeof v === 'number') ? v : '';
            if (val !== this._singleValue) {
                this._singleValue = val;
                this.onChange(val === '' ? null : val);
            }
        }
        this.updateSelectedLabel();
    }

    get showClearButton(): boolean {
        if (!this.allowAll) return false;

        if (this.multi) {
            // In multi-select, show only when at least one client is selected
            return Array.isArray(this._multiValue) && this._multiValue.length > 0;
        }

        // In single-select, show only when a specific client is selected
        return typeof this._singleValue === 'number';
    }

    selectedLabel = '';

    private onChange: (v: any) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit() {
        this.searchInput$
            .pipe(debounceTime(200))
            .subscribe(q => {
                this.applyFilter(q);
                this.cdr.markForCheck();
            });
    }

    ngOnChanges(ch: SimpleChanges) {
        if (ch.clients) {
            this.applyFilter(this.searchTerm);
            this.updateSelectedLabel();
            this.cdr.markForCheck();
        }

        // Sync the resting "explicitly deselected" state from the consumer's
        // own durable copy — see the explicitlyDeselected @Input doc.
        if (ch['explicitlyDeselected'] && this.selectAllChecksEveryClient) {
            this.allDeselected = this.explicitlyDeselected;
            this.updateSelectedLabel();
            this.cdr.markForCheck();
        }

        // Close dropdown if disabled
        if (ch['disabled'] && ch['disabled'].currentValue === true) {
            this.dropdownOpen = false;
        }
    }

    writeValue(v: number | number[] | '') {
        if (this.multi) {
            const arr = Array.isArray(v)
                ? v
                : (v === '' || v == null ? [] : [v as number]);
            this._multiValue = arr;
            // allDeselected is intentionally NOT reset here — an externally-
            // driven value can round-trip back through here (e.g. via a
            // consumer's router navigation) with a value indistinguishable
            // from the default empty selection. It's kept in sync solely via
            // the explicitlyDeselected @Input instead (see ngOnChanges).
        } else {
            const val = (typeof v === 'number') ? v : '';
            this._singleValue = val;
        }

        // Expand groups that contain any selected client ids
        this.activeGroups.clear();
        const selectedIds = this.multi
            ? this._multiValue
            : (typeof this._singleValue === 'number' ? [this._singleValue] : []);

        if (selectedIds.length) {
            const allGroups = [...this.clients[0], ...this.clients[1]];
            allGroups.forEach(grp => {
                if (!grp.clientId && grp.children?.some(ch => selectedIds.includes(ch.clientId))) {
                    this.activeGroups.add(grp.groupId);
                }
            });
        }

        this.updateSelectedLabel();
        this.rebuildOptions();

        // Ensure UI updates
        this.cdr.markForCheck();
    }



    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    /** Dropdown width. Normally at least the configured min width, but on small
     *  screens the toolbar filters shrink, and forcing the wider min width makes
     *  the dropdown overflow and get cut off — so there we match the trigger
     *  width (like the other filters). Always kept within the viewport. */
    private computeOverlayWidth(rect: DOMRect): number {
        const margin = 8;
        const smallScreen = window.innerWidth <= 850;
        const width = smallScreen ? rect.width : Math.max(rect.width, this.minDropdownWidth);
        return Math.min(width, window.innerWidth - 2 * margin);
    }

    private openDropdown() {
        if (!this.trigger) return;

        const rect = this.trigger.elementRef.nativeElement.getBoundingClientRect();
        this.overlayWidth = this.computeOverlayWidth(rect);
        this.dropUp = window.innerHeight - rect.bottom < window.innerHeight * 0.47;
        this.dropdownOpen = true;
        this.highlightedIndex = -1;
        this.searchTerm = '';
        this.applyFilter('');

        // In multi mode, initialize pending selection
        if (this.multi && this.showApply) {
            this._pendingMultiValue = [...this._multiValue];
            this._pendingAllDeselected = this.allDeselected;
        } else {
            this._pendingMultiValue = null;
            this._pendingAllDeselected = null;
        }

        setTimeout(() => this.searchInputRef?.nativeElement.focus(), 0);
    }

    closeDropdown() {
        this.dropdownOpen = false;

        // Cancel any pending changes in multi mode
        if (this.multi && this.showApply) {
            this._pendingMultiValue = null;
            if (this._pendingAllDeselected !== null) {
                this.allDeselected = this._pendingAllDeselected;
                this._pendingAllDeselected = null;
            }
        }
    }

    toggleDropdown() {
        if (this.disabled) {
            return;
        }

        if (!this.dropdownOpen) {
            this.openDropdown();
        } else {
            this.closeDropdown();
        }
    }

    applyFilter(term: string) {
        this.searchTerm = term.trim().toLowerCase();

        // if empty, show everything
        if (!this.searchTerm) {
            this.filtered = [...this.clients] as [Client[], Client[]];
            this.highlightedIndex = -1;
            return this.rebuildOptions();
        }

        // Helper to check if first word matches
        const matchesFirstWord = (label: string) => {
            const first = label.split(/\s+/)[0].toLowerCase();
            return first.startsWith(this.searchTerm);
        };

        const filterGroup = (grp: Client[]) =>
            grp
                .map(c => {
                    const label = c.displayName || c.clientName || '';
                    if (c.clientId) {
                        // only keep clients whose first word matches
                        return matchesFirstWord(label) ? c : null;
                    } else {
                        // for groups, keep only children whose first word matches
                        const kids = (c.children || []).filter(ch =>
                            matchesFirstWord(ch.displayName || ch.clientName || '')
                        );
                        return kids.length ? { ...c, children: kids } : null;
                    }
                })
                .filter(x => !!x) as Client[];

        this.filtered = [
            filterGroup(this.clients[0]),
            filterGroup(this.clients[1])
        ];

        this.highlightedIndex = -1;
        this.rebuildOptions();
    }

    private rebuildOptions() {
        const list: FlatOption[] = [];

        if (this.allowAll) {
            list.push({ type: 'all', label: this.allLabel });
        }

        const pushGroup = (grpArr: Client[]) => {
            grpArr.forEach(grp => {
                if (grp.clientId) {
                    list.push({
                        type: 'client',
                        label: grp.displayName || grp.clientName,
                        clientId: grp.clientId
                    });
                } else {
                    const childCount = Array.isArray(grp.children) ? grp.children.length : 0;
                    list.push({
                        type: 'groupLabel',
                        label: grp.groupName,
                        count: childCount,
                        groupId: grp.groupId
                    });
                    if (this.activeGroups.has(grp.groupId)) {
                        const kids = grp.children || [];
                        kids.forEach((ch, idx) => {
                            list.push({
                                type: 'child',
                                label: ch.displayName || ch.clientName,
                                clientId: ch.clientId,
                                // Mark the last child so we can draw a line under
                                // it, showing where the expanded group ends.
                                groupEnd: idx === kids.length - 1
                            });
                        });
                    }
                }
            });
        };

        // Pinned first
        pushGroup(this.filtered[0]);

        // Divider between pinned and the rest — but skip it if the pinned
        // section already ends with a group-end line (avoids a double line).
        if (this.filtered[0].length && this.filtered[1].length) {
            const last = list[list.length - 1];
            if (!last || !last.groupEnd) {
                list.push({ type: 'divider' });
            }
        }

        // Then the rest
        pushGroup(this.filtered[1]);

        this.optionsList = list;
        this.highlightedIndex = Math.min(this.highlightedIndex, list.length - 1);
    }

    // Check if a given client ID is selected
    private isIdSelected(id?: number): boolean {
        if (id == null) return false;

        if (this.multi) {
            return this.getEffectiveSelectionSource().includes(id);
        }

        return typeof this._singleValue === 'number' && this._singleValue === id;
    }


    private getMultiSelectionSource(): number[] {
        if (this.multi && this.showApply && this.dropdownOpen && this._pendingMultiValue) {
            return this._pendingMultiValue;
        }
        return this._multiValue;
    }

    /** Like getMultiSelectionSource(), but when selectAllChecksEveryClient is on
     *  and nothing has been explicitly deselected, an empty selection (the
     *  implicit "All") is expanded to every selectable id — so individual
     *  clients render as checked and can be toggled off one at a time. */
    private getEffectiveSelectionSource(): number[] {
        const source = this.getMultiSelectionSource();
        if (this.selectAllChecksEveryClient && (!source || source.length === 0) && !this.allDeselected) {
            return this.getAllSelectableIds();
        }
        return source || [];
    }

    public isOptionSelected(opt: FlatOption): boolean {
        // "All" is considered selected when nothing is selected
        if (opt.type === 'all') {
            if (!this.allowAll) return false;

            if (this.multi) {
                const source = this.getMultiSelectionSource();
                if (!source || source.length === 0) {
                    return !(this.selectAllChecksEveryClient && this.allDeselected);
                }
                return this.selectAllChecksEveryClient && this.isFullSelection(source);
            }

            return this._singleValue === '' || this._singleValue == null;
        }

        if (opt.type === 'client' || opt.type === 'child') {
            return this.isIdSelected(opt.clientId);
        }

        // Group labels and dividers are not selectable
        return false;
    }

    toggleGroup(groupId: number) {
        if (this.activeGroups.has(groupId)) {
            this.activeGroups.delete(groupId);
        } else {
            this.activeGroups.add(groupId);
        }
        this.rebuildOptions();
        const idx = this.optionsList.findIndex(o => o.type === 'groupLabel' && o.groupId === groupId);
        if (idx >= 0) {
            this.highlightedIndex = idx;
            this.scrollIntoView();
        }
    }

    /** Flat list of every selectable client id (top-level clients + all group children). */
    private getAllSelectableIds(): number[] {
        const ids: number[] = [];
        const collect = (arr: Client[]) => {
            arr.forEach(c => {
                if (c.clientId) {
                    ids.push(c.clientId);
                } else if (c.children) {
                    c.children.forEach(ch => {
                        if (ch.clientId != null) ids.push(ch.clientId);
                    });
                }
            });
        };
        collect(this.clients[0]);
        collect(this.clients[1]);
        return ids;
    }

    /** True when `source` contains every selectable id — i.e. equivalent to "All". */
    private isFullSelection(source: number[]): boolean {
        const allIds = this.getAllSelectableIds();
        return allIds.length > 0 && source.length === allIds.length && allIds.every(id => source.includes(id));
    }

    private getGroupChildIds(groupId: number): number[] {
        for (const grpArr of this.clients) {
            for (const grp of grpArr) {
                if (grp.groupId === groupId && grp.children) {
                    return grp.children.map(ch => ch.clientId).filter(id => id != null);
                }
            }
        }
        return [];
    }

    isGroupFullySelected(groupId: number): boolean {
        const childIds = this.getGroupChildIds(groupId);
        if (childIds.length === 0) return false;
        const source = this.getEffectiveSelectionSource();
        return childIds.every(id => source.includes(id));
    }

    isGroupPartiallySelected(groupId: number): boolean {
        const childIds = this.getGroupChildIds(groupId);
        if (childIds.length === 0) return false;
        const source = this.getEffectiveSelectionSource();
        const selectedCount = childIds.filter(id => source.includes(id)).length;
        return selectedCount > 0 && selectedCount < childIds.length;
    }

    onGroupCheckboxClick(groupId: number, event: Event): void {
        event.stopPropagation();
        const childIds = this.getGroupChildIds(groupId);
        if (childIds.length === 0) return;

        const allSelected = this.isGroupFullySelected(groupId);

        if (this.showApply) {
            const base = this.getEffectiveSelectionSource();
            const set = new Set(base);
            childIds.forEach(id => allSelected ? set.delete(id) : set.add(id));
            this._pendingMultiValue = Array.from(set);
            if (this.selectAllChecksEveryClient) {
                this.allDeselected = this._pendingMultiValue.length === 0;
            }
            this.cdr.markForCheck();
        } else {
            const base = this.getEffectiveSelectionSource();
            const set = new Set(base);
            childIds.forEach(id => allSelected ? set.delete(id) : set.add(id));
            this._multiValue = Array.from(set);
            if (this.selectAllChecksEveryClient) {
                this.allDeselected = this._multiValue.length === 0;
                this.allClientsDeselected.emit(this.allDeselected);
            }
            this.onChange(this._multiValue);
            this.updateSelectedLabel();
        }
    }

    onCheckboxClick(opt: FlatOption, event: Event): void {
      event.stopPropagation();

      this.onOptionClick(opt);
  }

    onOptionClick(opt: FlatOption) {
        this.onTouched();

        if (opt.type === 'all') {
            if (this.multi && this.selectAllChecksEveryClient) {
                // Acts as a master toggle: checked → every client selected,
                // unchecked → nothing selected.
                const currentlyAllSelected = this.isOptionSelected(opt);
                const nextValue = currentlyAllSelected ? [] : this.getAllSelectableIds();
                this.allDeselected = currentlyAllSelected;

                if (this.showApply) {
                    this._pendingMultiValue = nextValue;
                    this.cdr.markForCheck();
                } else {
                    this._multiValue = nextValue;
                    this.onChange(this._multiValue);
                    this.updateSelectedLabel();
                    this.allClientsDeselected.emit(this.allDeselected);
                }
                return;
            }

            if (this.multi && this.showApply) {
                this._pendingMultiValue = [];
                this.cdr.markForCheck();
            } else {
                this.clear();
                if (!this.multi) {
                    this.closeDropdown();
                }
            }
            return;
        }

        if (opt.clientId == null) {
            return;
        }

        if (this.multi) {
            this.toggleClientInMulti(opt.clientId);
            // keep dropdown open in multi-select mode
        } else {
            this.selectClient(opt.clientId);
            this.dropdownOpen = false;
        }
    }

    // Handle Apply button click in multi-select mode
    onApplyClick() {
        if (!this.multi) {
            return;
        }

        if (this.showApply) {
            let committed = this._pendingMultiValue ? [...this._pendingMultiValue] : [];
            // Selecting every client is equivalent to selecting "All" — normalize
            // to [] so downstream filtering behaves exactly as it did before
            // (no explicit client filter applied), unless the user excluded one.
            if (this.selectAllChecksEveryClient && this.isFullSelection(committed)) {
                committed = [];
            }
            this._multiValue = committed;
            this.onChange(this._multiValue);
            this.updateSelectedLabel();
            if (this.selectAllChecksEveryClient) {
                this.allClientsDeselected.emit(this.allDeselected);
            }
            this.applySelection.emit(this._multiValue || []);
            // The just-committed allDeselected state must stick — discard the
            // open-time snapshot so closeDropdown() doesn't revert it.
            this._pendingAllDeselected = null;
            this.closeDropdown();
        } else {
            // Fallback: if somehow used without showApply
            this.applySelection.emit(this._multiValue || []);
        }
    }

    private toggleClientInMulti(id: number) {
        if (this.multi && this.showApply) {
            const base = this.getEffectiveSelectionSource();
            const set = new Set(base);
            if (set.has(id)) {
                set.delete(id);
            } else {
                set.add(id);
            }
            this._pendingMultiValue = Array.from(set);
            if (this.selectAllChecksEveryClient) {
                this.allDeselected = this._pendingMultiValue.length === 0;
            }
            this.cdr.markForCheck();
            // No onChange, no label update yet
            return;
        }

        // Normal behaviour (multi without Apply)
        const base = this.getEffectiveSelectionSource();
        const set = new Set(base);
        if (set.has(id)) {
            set.delete(id);
        } else {
            set.add(id);
        }
        this._multiValue = Array.from(set);
        if (this.selectAllChecksEveryClient) {
            this.allDeselected = this._multiValue.length === 0;
            this.allClientsDeselected.emit(this.allDeselected);
        }
        this.onChange(this._multiValue);
        // don't emit changed in multi mode
        this.updateSelectedLabel();
    }

    selectClient(id: number) {
        this.value = id;
        this.changed.emit(id);
    }

    clear() {
        if (this.multi) {
            this._multiValue = [];
            this._pendingMultiValue = this.showApply ? [] : null;
            this.allDeselected = false;

            this.onChange(this._multiValue);
            if (this.selectAllChecksEveryClient) {
                this.allClientsDeselected.emit(false);
            }

            // In multi mode, treat this as "Apply All" immediately
            if (this.showApply) {
                this.applySelection.emit(this._multiValue || []);
            }
        } else {
            this._singleValue = '';
            this.onChange(null);
            this.changed.emit(null);
        }

        this.updateSelectedLabel();
        this.highlightedIndex = -1;
    }

    private moveHighlight(step: number) {
        const total = this.optionsList.length;
        let newIndex = this.highlightedIndex;

        do {
            newIndex = (newIndex + step + total) % total;
        } while (this.optionsList[newIndex]?.type === 'divider');

        this.highlightedIndex = newIndex;
        this.scrollIntoView();
    }

    onKeyDown(e: KeyboardEvent) {
        const total = this.optionsList.length;
        if (!this.dropdownOpen || total === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.moveHighlight(1);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.moveHighlight(-1);
        } else if (e.key === 'Enter') {
            const opt = this.optionsList[this.highlightedIndex];
            if (!opt) {
                return;
            }
            if (opt.type === 'groupLabel') {
                this.toggleGroup(opt.groupId!);
            } else if (opt.type !== 'divider') {
                this.onOptionClick(opt);
            }
        }
    }

    private scrollIntoView() {
        const arr = this.optionRefs.toArray();
        const elRef = arr[this.highlightedIndex];
        if (elRef) {
            elRef.nativeElement.scrollIntoView({ block: 'nearest' });
        }
    }

    private updateSelectedLabel() {
        if (this.multi) {
            if (!this._multiValue || this._multiValue.length === 0) {
                // An empty selection normally falls back to displaying allLabel
                // (e.g. "All Clients"). But when explicitly deselected via the
                // master toggle, that fallback would misleadingly claim
                // everything is still selected — say so instead.
                this.selectedLabel = (this.selectAllChecksEveryClient && this.allDeselected)
                    ? 'No clients selected'
                    : '';
                return;
            }
            if (this._multiValue.length === 1) {
                this.selectedLabel = this.resolveClientName(this._multiValue[0]) || '';
                return;
            }
            this.selectedLabel = `${this._multiValue.length} clients selected`;
            return;
        }

        if (typeof this._singleValue !== 'number') {
            this.selectedLabel = '';
            return;
        }

        this.selectedLabel = this.resolveClientName(this._singleValue) || '';
    }

    private resolveClientName(id: number): string | undefined {
        for (const grpArr of this.clients) {
            for (const c of grpArr) {
                if (c.clientId === id) {
                    return c.displayName || c.clientName;
                }
                if (c.children) {
                    const child = c.children.find(ch => ch.clientId === id);
                    if (child) {
                        return child.displayName || child.clientName;
                    }
                }
            }
        }
        return undefined;
    }

}
